/**
 * Application State for Poker Reborn
 */

import { signal, computed, Signal } from './Signal';
import type { ScreenType } from '@/types/ui';
import type { ConnectionState } from '@/types/protocol';
import type { PlayerProfile } from '@/types/game';

// =============================================================================
// APP STATE
// =============================================================================

export interface AppStateType {
  // Connection
  connectionState: Signal<ConnectionState>;
  isConnected: Signal<boolean>;

  // Authentication
  isAuthenticated: Signal<boolean>;
  playerId: Signal<string | null>;
  playerProfile: Signal<PlayerProfile | null>;
  playerName: Signal<string>;
  playerChips: Signal<number>;
  playerGold: Signal<number>;
  playerTickets: Signal<number>;

  // Navigation
  currentScreen: Signal<ScreenType>;
  previousScreen: Signal<ScreenType | null>;

  // Loading
  isLoading: Signal<boolean>;
  loadingMessage: Signal<string>;

  // Avatar
  playerAvatarId: Signal<number | null>;

  // Payload profile
  playerNickname: Signal<string | null>;
  playerDisplayName: Signal<string>;

  // XP / Level
  playerLevel: Signal<number>;
  playerXpPct: Signal<number>;

  // Account identity
  isGuest: Signal<boolean>;
  linkedProviders: Signal<string[]>;

  // Errors
  lastError: Signal<Error | null>;

  // Computed
  isReady: Signal<boolean>;
}

function createAppState(): AppStateType {
  // Connection
  const connectionState = signal<ConnectionState>('disconnected');
  const isConnected = computed(
    () => connectionState.get() === 'connected',
    [connectionState]
  );

  // Authentication
  const isAuthenticated = signal(false);
  const playerId = signal<string | null>(null);
  const playerProfile = signal<PlayerProfile | null>(null);
  const playerName = computed(
    () => playerProfile.get()?.name ?? 'Guest',
    [playerProfile]
  );
  const playerChips = signal(0);
  const playerGold = signal(0);
  const playerTickets = signal(0);

  // Avatar
  const playerAvatarId = signal<number | null>(null);

  // Payload profile
  const playerNickname = signal<string | null>(null);
  const playerDisplayName = computed(
    () => playerNickname.get() ?? playerProfile.get()?.name ?? 'Guest',
    [playerNickname, playerProfile]
  );

  // XP / Level
  const playerLevel = signal(1);
  const playerXpPct = signal(0);

  // Navigation
  const currentScreen = signal<ScreenType>('loading');
  const previousScreen = signal<ScreenType | null>(null);

  // Loading
  const isLoading = signal(false);
  const loadingMessage = signal('');

  // Account identity
  const isGuest = signal(false);
  const linkedProviders = signal<string[]>([]);

  // Errors
  const lastError = signal<Error | null>(null);

  // Computed
  const isReady = computed(
    () => isConnected.get() && isAuthenticated.get(),
    [isConnected, isAuthenticated]
  );

  return {
    connectionState,
    isConnected,
    isAuthenticated,
    playerId,
    playerProfile,
    playerName,
    playerChips,
    playerGold,
    playerTickets,
    playerAvatarId,
    playerNickname,
    playerDisplayName,
    playerLevel,
    playerXpPct,
    isGuest,
    linkedProviders,
    currentScreen,
    previousScreen,
    isLoading,
    loadingMessage,
    lastError,
    isReady,
  };
}

/** Global app state */
export const appState = createAppState();

// =============================================================================
// ACTIONS
// =============================================================================

/** Set connection state */
export function setConnectionState(state: ConnectionState): void {
  appState.connectionState.set(state);
}

/** Set authenticated */
export function setAuthenticated(
  playerId: string,
  profile: PlayerProfile,
  chips: number
): void {
  appState.playerId.set(playerId);
  appState.playerProfile.set(profile);
  appState.playerChips.set(chips);
  appState.isAuthenticated.set(true);
}

/** Clear authentication */
export function clearAuthentication(): void {
  appState.playerId.set(null);
  appState.playerProfile.set(null);
  appState.playerChips.set(0);
  appState.playerGold.set(0);
  appState.playerTickets.set(0);
  appState.isGuest.set(false);
  appState.linkedProviders.set([]);
  appState.isAuthenticated.set(false);
}

/** Set account identity (guest status + linked providers) */
export function setAccountIdentity(isGuest: boolean, linkedProviders: string[]): void {
  appState.isGuest.set(isGuest);
  appState.linkedProviders.set(linkedProviders);
}

/** Navigate to screen */
export function navigateToScreen(screen: ScreenType): void {
  appState.previousScreen.set(appState.currentScreen.get());
  appState.currentScreen.set(screen);
}

/** Go back to previous screen */
export function goBack(): void {
  const previous = appState.previousScreen.get();
  if (previous) {
    appState.currentScreen.set(previous);
    appState.previousScreen.set(null);
  }
}

/** Set loading state */
export function setLoading(loading: boolean, message = ''): void {
  appState.isLoading.set(loading);
  appState.loadingMessage.set(message);
}

/** Set error */
export function setError(error: Error | null): void {
  appState.lastError.set(error);
}

/** Update player chips */
export function updateChips(chips: number): void {
  appState.playerChips.set(chips);
}

/** Update player gold */
export function updateGold(gold: number): void {
  appState.playerGold.set(gold);
}

/** Update player tickets */
export function updateTickets(tickets: number): void {
  appState.playerTickets.set(tickets);
}

/** Set player nickname (from profile data) */
export function setPlayerNickname(nickname: string | null): void {
  appState.playerNickname.set(nickname);
}

/** Update XP state (level and progress percentage) */
export function updateXpState(level: number, pct: number): void {
  appState.playerLevel.set(level);
  appState.playerXpPct.set(pct);
}

// =============================================================================
// CONVENIENCE FUNCTIONS (for controllers)
// =============================================================================

/** Set screen (alias for navigateToScreen) */
export function setScreen(screen: ScreenType): void {
  navigateToScreen(screen);
}

/** Set connected state */
export function setConnected(connected: boolean): void {
  appState.connectionState.set(connected ? 'connected' : 'disconnected');
}

/** Set player profile and chips */
export function setPlayer(player: PlayerProfile | null): void {
  if (player) {
    appState.playerProfile.set(player);
    appState.playerId.set(player.id);
  } else {
    appState.playerProfile.set(null);
    appState.playerId.set(null);
    appState.isAuthenticated.set(false);
  }
}

